import { IonIcon, IonSpinner } from '@ionic/react'
import { ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import { ProductConsumptionCosts } from '@vae/server/src/modules/ExternalAPIs/Synerall/utils/costCalculators'
import { SpotAverage } from '@vae/server/src/types/synerallApiTypes'
import { bulbOutline } from 'ionicons/icons'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../analytics/utils/firebaseUtils'
import { ModalContext } from '../../contexts/ModalContext'
import { ObjectContext } from '../../contexts/objectContext/ObjectContext'
import { UserContext } from '../../contexts/UserContext'
import { trpc } from '../../modules/trpc'
import ProductShowcaseCard from './components/ProductShowcaseCard'
import { sortOfferedProducts } from './utils/sortOfferedProducts'

const GetOurContract = () => {
	const { id } = useContext(ObjectContext)
	const { isAnonymousUser, setPostAuthProductId, setPostAuthRedirect } = useContext(UserContext)
	const { setActiveOffer, insertModal } = useContext(ModalContext)

	const { t } = useTranslation('modals_getOurContract')

	const [disclaimerOpen, setDisclaimerOpen] = useState<boolean>(false)

	const { data, isLoading } = trpc.synerall.productOffers.useQuery(
		{ objectId: id },
		{ onSuccess: products => sortOfferedProducts(products) }
	)

	const onClickOffer = async (offer: ProductConsumptionCosts) => {
		logEvent('click-open-offer', offer)

		setActiveOffer(offer)
		setPostAuthProductId(String(offer.product?.ID ?? null))
		setPostAuthRedirect('createContract')
		insertModal('createContract')
	}

	if (isLoading || !data?.offers?.length || !data.currentCosts) return <IonSpinner />

	const currentCost = data.currentCosts.totalCost
	const { totalKwhConsumed, totalDays: days } = data.currentCosts
	const kwh = totalKwhConsumed.toFixed(0)
	return (
		<div id='get-our-contract'>
			<div className='contracts'>
				<div className='header-block text-brand-secondary-100-light dark:text-brand-primary-200-dark border-[3px] border-brand-primary-100-light dark:border-brand-primary-200-dark dark:bg-[linear-gradient(75deg,_#3cb49f_0%,_#cae3cc_100%)] bg-[linear-gradient(75deg,_#d6e4ff_0%,_#c9f7d3_100%)]'>
					<IonIcon className='header-icon' icon={bulbOutline} />
					<span className='header-text'>{t('title')}</span>
				</div>

				{!isAnonymousUser && (
					<div className='header-disclaimer-block text-brand-secondary-100-light dark:text-brand-primary-200-dark border-[3px] border-brand-primary-100-light dark:border-brand-primary-200-dark dark:bg-[linear-gradient(75deg,_#3cb49f_0%,_#cae3cc_100%)] bg-[linear-gradient(75deg,_#d6e4ff_0%,_#c9f7d3_100%)]'>
						<Accordion
							className='text-brand-secondary-100-light dark:text-brand-primary-200-dark dark:bg-[linear-gradient(75deg,_#3cb49f_0%,_#cae3cc_100%)] bg-[linear-gradient(75deg,_#d6e4ff_0%,_#c9f7d3_100%)]'
							expanded={disclaimerOpen}
							onChange={() => setDisclaimerOpen(!disclaimerOpen)}
						>
							<AccordionSummary expandIcon={<ExpandMore />}>{t('disclaimer1')}</AccordionSummary>
							<AccordionDetails className='header-disclaimer-bg'>
								<div className='header-disclaimer-container header-disclaimer-bg'>
									<span>{t('disclaimer2', { days, kwh })}</span>
									<span>{t('disclaimer3')}</span>
									<span className='title'>{t('disclaimer4')}</span>
									<span>{t('disclaimer5')}</span>
									<span className='title'>{t('disclaimer6')}</span>
									<span>{t('disclaimer7', { months: data.spotAverages.length, days, kwh })}</span>
									<span className='title'>{t('disclaimer8')}</span>
									<span>{t('disclaimer9', { days, kwh })}</span>
									<span>{t('disclaimer10')}</span>
									<span>
										<ul>
											{data.spotAverages.map((x: SpotAverage, i) => (
												<li key={i}>{`${x.start}-${x.end} - ${x.cost.toFixed(2)} c/kWh`}</li>
											))}
										</ul>
									</span>
									<span>{t('disclaimer11')}</span>
								</div>
							</AccordionDetails>
						</Accordion>
					</div>
				)}

				{data.offers.map(
					(offer: ProductConsumptionCosts, index: number) =>
						offer.product && (
							<ProductShowcaseCard
								key={index}
								product={offer.product}
								savings={currentCost - offer.costs.totalCost}
								maxKwh={offer.kwhLimit}
								isSpot={offer.costs.isSpot}
								onClick={() => onClickOffer(offer)}
							/>
						)
				)}
			</div>
		</div>
	)
}

export default GetOurContract
